import {
    useState, useEffect, SetStateAction, Dispatch,
} from 'react';
import { ProFormaGenericRow } from 'views/ProFormaTable/components/ProFormaGenericTable/types';
import ProFormaGenericTable from 'views/ProFormaTable/components/ProFormaGenericTable/ProFormaGenericTable';
import { isAllApartmentModels, isStudentHousingModel } from 'helpers/proForma';
import { BASE_VALUATION_COLUMNS, BASE_STABILIZATION_COLUMNS } from './constants';
import './ProFormaValuation.scss';
import { getDispositionPeriodSubtitle, getColumnTitle, getUnitTypeOptions } from '../helpers';
import { getStabilizationColumnHeaderText } from './helpers';
import { useProFormaSocketContext } from '../../socketContext/ProFormaSocketProvider';
import UnitTypeHeader from '../UnitTypeHeaderDropdown/UnitTypeHeader';
import {
    APARTMENT_UNIT_TYPE_NAME, NON_APARTMENT_UNIT_TYPE_NAME, STUDENT_HOUSING_UNIT_TYPE_NAME,
} from '../constants';
import { SelectedType, StabilizedKey } from './types';

const isDash = (value: string): boolean => value === '-' || value === '' || value === undefined || value === null;

export interface IProFormaValuationProps {
    selectedType: string;
    setSelectedType: Dispatch<SetStateAction<string>>;
}

const ProFormaValuation = ({ selectedType, setSelectedType }: IProFormaValuationProps) => {
    const {
        disposition, proForma, showCents, operatingInfo,
    } = useProFormaSocketContext();
    const [valuationRows, setValuationRows] = useState<ProFormaGenericRow[]>([]);

    const unitTypeOptions = getUnitTypeOptions(operatingInfo, proForma);

    useEffect(() => {
        const rows: ProFormaGenericRow[] = [
            {
                id: 'noi',
                title: 'Net operating income',
                untrended: disposition?.netOperatingIncome?.untrended?.toString() || 0,
                trended: disposition?.netOperatingIncome?.trended?.toString() || 0,
                sale: disposition?.netOperatingIncome?.sale?.toString() || 0,
            },
            {
                id: 'capRate',
                title: 'Cap rate',
                untrended: disposition?.marketCapitalizationRate.current || 0,
                trended: disposition?.marketCapitalizationRate.trended || 0,
                sale: disposition?.marketCapitalizationRate.onSale || 0,
                format: 'percentage',
            },
        ];
        setValuationRows(rows);
    }, [disposition]);

    useEffect(() => {
        let newType: string = NON_APARTMENT_UNIT_TYPE_NAME.GSF;
        if (isStudentHousingModel(proForma?.proFormaModelId)) {
            newType = STUDENT_HOUSING_UNIT_TYPE_NAME.BED;
        } else if (isAllApartmentModels(proForma?.proFormaModelId)) {
            newType = APARTMENT_UNIT_TYPE_NAME.UNIT;
        }
        setSelectedType(newType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [proForma?.proFormaModelId]);

    const valuationColumns = [
        ...BASE_VALUATION_COLUMNS.map(col => {
            if (['untrended', 'trended', 'sale'].includes(col.key)) {
                return {
                    ...col,
                    header: (
                        <div className="ProFormaForecastTable__header">
                            <div>{getColumnTitle(col.key)}</div>
                            <small className="text-muted">{getDispositionPeriodSubtitle(col.key, disposition)}</small>
                        </div>
                    ),
                };
            }
            return col;
        }),
    ];

    const stabilizationColumns = [
        ...BASE_STABILIZATION_COLUMNS.map(col => {
            if (col.key === 'title') {
                return {
                    ...col,
                    header: (
                        <div className="ProFormaValuation__header">
                            <div>Stabilized value</div>
                            <div className="d-flex align-items-center text-muted">
                                <span className="mr-2">View as:</span>
                                <UnitTypeHeader
                                    options={unitTypeOptions}
                                    selectedType={selectedType}
                                    onTypeChange={setSelectedType as any}
                                />
                            </div>
                        </div>
                    ),
                };
            }
            if (['untrended', 'trended', 'sale'].includes(col.key)) {
                const headerText = getStabilizationColumnHeaderText(
                    col.key as StabilizedKey, disposition, showCents, selectedType as SelectedType,
                );
                return {
                    ...col,
                    header: (
                        <div className="ProFormaValuation__header">
                            <div className={isDash(headerText.title) ? 'text-muted' : ''}>
                                {headerText.title}
                            </div>
                            <div className="d-flex align-items-center font-weight-normal">
                                <span className="text-muted">{headerText.subtitlePrefix}</span>
                                <span className="ml-1">{headerText.subtitle}</span>
                            </div>
                        </div>
                    ),
                };
            }
            return col;
        }),
    ];

    return (
        <div className="d-flex flex-column p-3">
            <ProFormaGenericTable
                columns={valuationColumns}
                rows={valuationRows}
                setRows={setValuationRows}
                hideAddButton
                hideTotalFooter
                hideCashFlowTotals
                fixedColumnsMaxWidth="100%"
            />
            <ProFormaGenericTable
                columns={stabilizationColumns}
                rows={[]}
                setRows={() => { }}
                hideTotalFooter
                hideAddButton
                hideCashFlowTotals
                fixedColumnsMaxWidth="100%"
            />
        </div>
    );
};

export default ProFormaValuation;
